import { Dispatch, FC, ReactNode, SetStateAction, UIEvent, useRef, useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { MeterGroup } from 'primereact/metergroup';
import { SkillStatesLabel } from '../../models/SkillState';
import { ProgressBar } from '../../models/ProgressBar';
import { Toast } from 'primereact/toast';
import { VirtualScroller, VirtualScrollerTemplateOptions } from 'primereact/virtualscroller';
import { classNames } from 'primereact/utils';
import { Skeleton } from 'primereact/skeleton';
import { Skill } from '../../models/SupportSkills';
import { Skill as SingleSkill, SkillResult } from '../../models/Skill';
import SingleUnitSkillDialog from './SingleUnitSkillDialog';
import capitalize from '../../utils/capitalize';
import { OutsideClickDetector } from '../OutsideClickDetector/OutsideClickDetector';

interface Props {
  values: ProgressBar[];
  setStudentSkillProgressDetailInput?: Dispatch<
    SetStateAction<
      | {
          studentId: string;
          skillResult: SkillResult;
          count: number;
        }
      | undefined
    >
  >;
  list: Skill[];
  lazyLoading: boolean;
  onScroll?(event: UIEvent<HTMLElement>): void;
  setSkills: Dispatch<SetStateAction<Skill[]>>;
  setLazyLoading: Dispatch<SetStateAction<boolean>>;
  studentSkillProgressDetailInput?: {
    studentId: string;
    skillResult: SkillResult;
    count: number;
  };
  setPageInfo: Dispatch<
    SetStateAction<
      | {
          endCursor: string | null | undefined;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor: string | null | undefined;
        }
      | undefined
    >
  >;
}

const SingleUnitProgressBarStudentsProgressTable: FC<Props> = ({
  values,
  setStudentSkillProgressDetailInput,
  list,
  lazyLoading,
  onScroll,
  setSkills,
  setLazyLoading,
  studentSkillProgressDetailInput,
  setPageInfo,
}) => {
  const toast = useRef<Toast>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const [skill, setSkill] = useState<SingleSkill>();
  const [visibleDialog, setVisibleDialog] = useState<boolean>(false);

  const meter = (params: ProgressBar): ReactNode => {
    const handleClickTieredMenu = () => {
      setStudentSkillProgressDetailInput?.({
        studentId: params?.studentId || '',
        skillResult: (params?.key || '') as SkillResult,
        count: params?.count ?? 0,
      });
      setVisible(true);
    };

    return (
      <>
        {params?.value > 0 && <Tooltip target={`.${params?.className}`} mouseTrack position="top" />}
        <span
          key={params?.label}
          className={`${params?.className} bar-md`}
          onClick={handleClickTieredMenu}
          data-pr-tooltip={`${params?.label}: ${params?.count} \n \n`}
          style={{
            width: params?.value + '%',
            borderRight: params?.value === 0 || params?.label === SkillStatesLabel.NOT_STARTED ? '' : '2px solid white',
          }}
        />
      </>
    );
  };

  const itemTemplate = (item: Skill, options: VirtualScrollerTemplateOptions) => {
    const className = classNames('flex align-items-center p-2 p-menuitem-link', {
      'surface-hover': options.odd,
    });

    return (
      <div className="p-menuitem-content">
        <a
          className={className}
          style={{ height: options.props.itemSize + 'px' }}
          onClick={() => {
            setVisibleDialog(true);
            setSkill(item as SingleSkill);
          }}
        >
          <span className="p-menuitem-text">{item?.name}</span>
        </a>
      </div>
    );
  };

  const loadingTemplate = (options: VirtualScrollerTemplateOptions) => {
    const className = classNames('flex align-items-center p-2', {
      odd: options.odd,
    });
    return (
      <div className={className} style={{ height: '50px' }}>
        <Skeleton width={options.even ? '60%' : '50%'} height="1.3rem" />
      </div>
    );
  };

  const handleCopy = async () => {
    setVisible(false);
    try {
      const skillNamesToString: string | undefined = list?.map(skill => skill?.name)?.join(', ');
      if (skillNamesToString) {
        await navigator.clipboard.writeText(skillNamesToString);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Skills have been copied to clipboard!',
          life: 3000,
        });
      }
    } catch (_error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Skills have not been copied to clipboard',
        life: 3000,
      });
    }
  };

  return (
    <div className="relative">
      <Toast ref={toast} position="top-right" appendTo={document.body} />
      <MeterGroup
        max={70}
        min={0}
        className="nc_metergroup nc_metergroup-fixed-width"
        values={values}
        meter={meter as () => ReactNode}
      />
      {visible && (
        <OutsideClickDetector
          onOutsideClick={() => {
            setVisible(false);
            setStudentSkillProgressDetailInput?.(undefined);
            setSkills([]);
            setLazyLoading(true);
            setPageInfo(undefined);
          }}
        >
          <div className="flex justify-content-center flex-column absolute top-1 nc_metergroup-menu">
            <div
              className={`w-full p-2 flex flex-column align-content-center  justify-content-center nc_metergroup-menu-header ${studentSkillProgressDetailInput?.skillResult
                ?.replace('_', '-')
                ?.toLowerCase()}`}
            >
              <h4 className="text-center">
                {capitalize(studentSkillProgressDetailInput?.skillResult?.replace('_', ' ')?.toLowerCase() || '')}
              </h4>
              <span className="text-center">({studentSkillProgressDetailInput?.count} skills)</span>
            </div>

            <div className="bg-white p-2 flex align-items-center gap-1" onClick={handleCopy}>
              <a className="nc_icon-nc_copy icon-sm"></a>
              <a>Copy list of skill(s)</a>
            </div>
            <VirtualScroller
              items={list}
              itemSize={50}
              itemTemplate={itemTemplate}
              lazy
              onScroll={onScroll}
              loadingTemplate={loadingTemplate}
              showLoader
              loading={lazyLoading}
              className="border-1 surface-border border-round w-full h-full"
            />
          </div>
        </OutsideClickDetector>
      )}
      {visibleDialog && skill && (
        <SingleUnitSkillDialog visible={visibleDialog} setVisible={setVisibleDialog} skill={skill} />
      )}
    </div>
  );
};

export default SingleUnitProgressBarStudentsProgressTable;
